import { format, toZonedTime } from 'date-fns-tz';
import { Image } from 'expo-image';
import * as React from 'react';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type {
  Extra,
  Payment,
  Refund,
  Reservation,
  ReservationAdjustment,
  ReservationRate,
} from '@/api';
import { formatNumber, getCarImages, snakeToTitleCase } from '@/core/utils';
import { PINKLING } from '@/style/colors';
import { Button, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { ChevronRight } from '@/ui/icons/chevron-right';

import type { TripBreakdownTabOption } from '../[id]';

export interface ReceiptProps {
  reservation: Reservation;
  onTabChange: (tab: TripBreakdownTabOption) => void;
}

const Receipt = ({ reservation, onTabChange }: ReceiptProps) => {
  const { styles, theme } = useStyles(stylesheet);
  // TODO: waiting on receipt design from Mate
  const handleViewPdf = () => {};

  const handleViewIncidentals = () => {
    onTabChange('incidentals');
  };

  const handleViewTolls = () => {
    onTabChange('tolls');
  };

  const formatDate = (d: Date) => {
    if (!d) return;

    const dateFormatted = format(d, 'MM.dd.yyyy');

    return dateFormatted;
  };

  const formatTime = (d: Date) => {
    if (!d) return;

    const timeFormatted = format(d, 'hh:mm a');

    return timeFormatted;
  };

  const renderExtra = (extra: Extra) => {
    return extra.recurring ? (
      <>
        <Text type="bodyRegular">{extra.title}</Text>

        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            {`$${extra.amount}/day`}
          </Text>

          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            {`x${reservation?.numDays} days`}
          </Text>

          <Text type="bodyRegular">{`$${
            extra.amount * reservation?.numDays
          }`}</Text>
        </View>
      </>
    ) : (
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Text type="bodyRegular">{extra.title}</Text>
        <Text type="bodyRegular">{`$${extra.amount}`}</Text>
      </View>
    );
  };

  return (
    <View style={[styles.container]}>
      <View style={{ gap: 16 }}>
        <Image
          source={{
            uri:
              reservation.car && getCarImages(reservation.car.images).length > 0
                ? getCarImages(reservation.car.images)[0].url
                : '',
          }}
          style={{ width: '100%', aspectRatio: 16 / 9 }}
          contentFit="cover"
        />

        <View style={{ gap: 16, padding: 16 }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Text tx="receipt.rentalDetails" type="title1" />

            {/* <Button
              tx="receipt.viewPdf"
              type="secondaryExtraSmall"
              onPress={handleViewPdf}
              rightIcon={<ArrowRight />}
              style={{ width: 100 }}
            /> */}
          </View>

          <View style={{ gap: 8 }}>
            <Text
              tx="receipt.tripId"
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            />

            <Text type="bodyRegular">{reservation.id}</Text>
          </View>

          <View style={{ gap: 8 }}>
            <Text
              tx="receipt.pickup"
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            />

            <Text type="bodyRegular">{`${formatDate(
              toZonedTime(
                reservation.startTime,
                reservation.pickupLocation.timezone
              )
            )} | ${formatTime(
              toZonedTime(
                reservation.startTime,
                reservation.pickupLocation.timezone
              )
            )}`}</Text>
          </View>

          <View style={{ gap: 8 }}>
            <Text
              tx="receipt.return"
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            />

            <Text type="bodyRegular">{`${formatDate(
              toZonedTime(
                reservation.endTime,
                reservation.returnLocation.timezone
              )
            )} | ${formatTime(
              toZonedTime(
                reservation.endTime,
                reservation.returnLocation.timezone
              )
            )}`}</Text>
          </View>

          <View style={{ gap: 8 }}>
            <Text
              tx={
                reservation.customPickupLocation
                  ? 'receipt.deliveryLocation'
                  : 'receipt.pickupLocation'
              }
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            />

            <View>
              <Text type="bodyRegular">
                {reservation.customPickupLocation ||
                  reservation.pickupLocation.address}
              </Text>
            </View>
          </View>

          <View style={{ gap: 8 }}>
            <Text
              tx="receipt.returnLocation"
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            />

            <View>
              <Text type="bodyRegular">
                {reservation.customReturnLocation ||
                  reservation.returnLocation.address}
              </Text>
            </View>
          </View>

          {reservation?.cancellationPolicy ? (
            <View>
              <Text type="bodyRegular">
                {reservation.cancellationPolicy.title} booking
              </Text>
            </View>
          ) : null}
        </View>
      </View>

      <View
        style={{
          backgroundColor: theme.colors.card.default.borderColor,
          height: 1,
        }}
      />

      <View style={{ padding: 16, gap: 16 }}>
        <Text tx="receipt.rentalBreakdown" type="title1" />

        <Text tx="receipt.vehicle" type="title2" />

        <View style={{ gap: 4 }}>
          <Text type="bodyRegular">{`${
            reservation?.car.brandName
          } ${snakeToTitleCase(reservation?.car.modelName)} ${snakeToTitleCase(
            reservation?.car.modelType
          )}`}</Text>

          <View style={{ gap: 4 }}>
            {reservation?.reservationRates.map((rate: ReservationRate) => (
              <View key={rate.id}>
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {`$${formatNumber(rate.dailyRate)}/day`}
                  </Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {`x${rate.numDays} days`}
                  </Text>

                  <Text type="bodyRegular">{`$${formatNumber(
                    rate.numDays * rate.dailyRate
                  )}`}</Text>
                </View>

                {rate.discountType && (
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.chip.brand.color }}
                    >
                      {rate.discountType}
                    </Text>

                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.chip.brand.color }}
                    >
                      {`-$${formatNumber(rate.discountAmount)}`}
                    </Text>
                  </View>
                )}
              </View>
            ))}
          </View>
        </View>

        {reservation.mileagePlan ? (
          <View style={{ gap: 4 }}>
            <Text type="title2">Mileage</Text>

            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Text type="bodyRegular">
                {`${
                  reservation.mileagePlan.totalMilesAllowance === -1
                    ? 'Unlimited'
                    : reservation.mileagePlan.totalMilesAllowance
                } miles`}
              </Text>

              <Text type="bodyRegular">
                {`${
                  reservation.mileagePlan.totalMilesAllowance === -1
                    ? 'Unlimited'
                    : `$${formatNumber(reservation.mileagePlanCost)}`
                }`}
              </Text>
            </View>
          </View>
        ) : null}

        <View style={{ gap: 8 }}>
          <Text type="title2" tx="receipt.extrasAndServices" />

          <View style={{ gap: 8 }}>
            {reservation?.initiationFee ? (
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular">Initiation Fee</Text>

                <Text type="bodyRegular">
                  {`$${formatNumber(reservation?.initiationFee / 100)}`}
                </Text>
              </View>
            ) : null}

            {reservation?.totalDeliveryCost ? (
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular">Delivery Fee</Text>

                <Text type="bodyRegular">
                  {`$${formatNumber(reservation.totalDeliveryCost)}`}
                </Text>
              </View>
            ) : null}

            {reservation?.extras
              .filter((extra: Extra) => extra.title !== 'Custom Delivery')
              .map((extra: Extra) => (
                <View key={extra.id}>{renderExtra(extra)}</View>
              ))}

            {reservation?.extraMiles > 0 && (
              <View>
                <Text type="bodyRegular" tx="receipt.additionalMiles" />

                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {`$${formatNumber(
                      reservation?.additionalMilesCost / reservation?.extraMiles
                    )}/miles`}
                  </Text>

                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {`x${reservation?.extraMiles} miles`}
                  </Text>

                  <Text type="bodyRegular">{`$${formatNumber(
                    reservation?.additionalMilesCost
                  )}`}</Text>
                </View>
              </View>
            )}
          </View>
        </View>

        <View style={{ gap: 8 }}>
          <Text type="title2" tx="receipt.insurance" />

          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Text type="bodyRegular">{reservation?.insurance.category}</Text>

            <Text type="bodyRegular">{`$${formatNumber(
              reservation?.insuranceCost
            )}`}</Text>
          </View>
        </View>

        {reservation?.totalCancellationPolicyCost > 0 ? (
          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Text type="bodyRegular">
              {reservation?.cancellationPolicy?.title} booking
            </Text>

            <Text type="bodyRegular">{`$${formatNumber(
              reservation?.totalCancellationPolicyCost
            )}`}</Text>
          </View>
        ) : null}

        <View style={{ gap: 8 }}>
          <Text type="title2" tx="receipt.charging" />

          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Text type="bodyRegular" tx="receipt.supercharging" />

            <Text type="bodyRegular">{`$${formatNumber(
              reservation?.resolvedChargingAmount
            )}`}</Text>
          </View>
        </View>

        {reservation?.incidentalsCost ? (
          <View style={{ gap: 8 }}>
            <View
              style={{ flexDirection: 'row', justifyContent: 'space-between' }}
            >
              <Text type="title2" tx="receipt.incidentals" />

              <Button
                tx="receipt.viewIncidentals"
                type="secondaryExtraSmall"
                onPress={handleViewIncidentals}
                rightIcon={
                  <Icon
                    type="naked"
                    icon={<ChevronRight />}
                    fillColor={theme.colors.icon.color}
                    width={10}
                    height={10}
                  />
                }
                style={{ width: 140 }}
              />
            </View>

            <View
              style={{ flexDirection: 'row', justifyContent: 'space-between' }}
            >
              <Text type="bodyRegular" tx="receipt.incidentals" />

              <Text type="bodyRegular">{`$${formatNumber(
                reservation?.incidentalsCost
              )}`}</Text>
            </View>

            <View
              style={{
                marginTop: 16,
                width: '100%',
                backgroundColor: theme.colors.background_3,
                height: 1,
                alignSelf: 'center',
              }}
            />
          </View>
        ) : null}

        <View style={{ gap: 8 }}>
          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Text type="title2" tx="receipt.tolls" />

            {reservation?.resolvedTollsAmount ? (
              <Button
                tx="receipt.viewTolls"
                type="secondaryExtraSmall"
                onPress={handleViewTolls}
                rightIcon={
                  <Icon
                    type="naked"
                    icon={<ChevronRight />}
                    fillColor={theme.colors.icon.color}
                    width={10}
                    height={10}
                  />
                }
                style={{ width: 120 }}
              />
            ) : null}
          </View>

          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Text type="bodyRegular" tx="receipt.tolls" />

            <Text type="bodyRegular">{`$${formatNumber(
              reservation?.resolvedTollsAmount
            )}`}</Text>
          </View>
        </View>
      </View>

      <View
        style={{
          backgroundColor: theme.colors.card.default.borderColor,
          height: 1,
        }}
      />

      <View style={{ padding: 16, gap: 16 }}>
        <Text tx="receipt.payments" type="title1" />

        <View>
          <View style={{ gap: 8 }}>
            {reservation?.payments.map((payment: Payment) => (
              <View
                key={payment.id}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text
                  type="bodyRegular"
                  style={payment.isHold ? { color: theme.colors.pending } : {}}
                >
                  {payment.memo}
                </Text>

                <Text type="bodyRegular">{`$${formatNumber(
                  payment.amount
                )}`}</Text>
              </View>
            ))}

            {reservation?.refunds.map((refund: Refund) => (
              <View
                key={refund.id}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular">{refund.memo}</Text>

                <Text type="bodyRegular">{`-$${formatNumber(
                  refund.amount
                )}`}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={{ gap: 8 }}>
          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Text type="bodyRegular" tx="receipt.subtotal" />

            <Text type="bodyRegular">{`$${formatNumber(
              reservation?.subtotal
            )}`}</Text>
          </View>

          {/* <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Text type="bodyRegular" tx="receipt.salesTax" />

            <Text type="bodyRegular">{`$${reservation?.salesTax}`}</Text>
          </View> */}

          {reservation?.adjustments ? (
            <View
              style={{ flexDirection: 'row', justifyContent: 'space-between' }}
            >
              <Text
                type="bodyRegular"
                tx="receipt.adjustments"
                style={{ color: PINKLING }}
              />

              <Text
                type="bodyRegular"
                style={{ color: PINKLING }}
              >{`-$${formatNumber(reservation?.adjustments)}`}</Text>
            </View>
          ) : null}

          {reservation?.reservationAdjustments?.map(
            (adjustment: ReservationAdjustment) => (
              <View
                key={adjustment.id}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular" style={{ color: PINKLING }}>
                  {adjustment.adjustmentType === 'non_refundable_shortened_trip'
                    ? 'Shortened Trip'
                    : snakeToTitleCase(adjustment.adjustmentType)}
                </Text>

                <Text type="bodyRegular" style={{ color: PINKLING }}>{`${
                  adjustment.amount < 0 ? '-' : ''
                }$${formatNumber(Math.abs(adjustment.amount))}`}</Text>
              </View>
            )
          )}

          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Text type="bodyRegular" tx="receipt.total" />

            <Text type="bodyBold">{`$${formatNumber(
              reservation?.totalCost
            )}`}</Text>
          </View>

          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Text type="title2" tx="receipt.outstandingBalance" />

            <Text type="title2">{`$${formatNumber(
              reservation?.outstandingBalance
            )}`}</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  container: {
    backgroundColor: theme.colors.background_2,
    borderRadius: 24,
    flexDirection: 'column',
    gap: 16,
    borderWidth: 1,
    borderColor: theme.colors.card.default.borderColor,
  },
}));

export default Receipt;
